/**
 * Discovery: the signed-in user's interactive pages ("My Pages") and the pages
 * they've pinned ("Shared/Pinned"). Same-origin via the Worker proxy
 * (`/api/eve/v1/*`); the staff/guest bearer is attached server-side and the
 * session cookie rides along.
 *
 * Best-effort UI, like `conversations.ts`: a 401 (signed out) or any failure
 * resolves to an empty list / `false` rather than throwing into the shell.
 */

/** Mirrors eve's `PageSummary` (agent/lib/conversations/store.ts) — the fields
 *  the sidebar lists render. `pinnedAt` is present only on pinned results. */
export interface PageSummary {
  id: string;
  title: string | null;
  url: string;
  visibility: 'private' | 'domain' | 'public';
  createdAt: string;
  updatedAt: string;
  refreshInterval: 'manual' | 'daily' | null;
  lastRefreshedAt: string | null;
  lastStatus: 'ok' | 'failed' | null;
  paused: boolean;
  pinnedAt?: string;
  /** The conversation that published it; only on the user's own pages. */
  sessionId?: string;
  /** Whether it survives that conversation being deleted. Server-computed: a
   *  client cannot derive it, since another user's pin is deliberately not
   *  visible in the owner's own listing. Absent from an older agent, which the
   *  caller must read as "unknown", never as "not promoted". */
  promoted?: boolean;
}

async function getList(path: string, key: 'pages' | 'pins'): Promise<PageSummary[]> {
  try {
    const res = await fetch(path, { credentials: 'same-origin' });
    if (!res.ok) return [];
    const body = (await res.json()) as Record<string, unknown>;
    const list = body?.[key];
    return Array.isArray(list) ? (list as PageSummary[]) : [];
  } catch {
    return [];
  }
}

/** Pages this user created, newest activity first (server-ordered by updated_at). */
export function listMyPages(): Promise<PageSummary[]> {
  return getList('/api/eve/v1/pages', 'pages');
}

/** Pages this user has pinned (most-recently-pinned first). */
export function listPinnedPages(): Promise<PageSummary[]> {
  return getList('/api/eve/v1/pins', 'pins');
}

/**
 * Pins a page to the user's sidebar. The server confirms the user can VIEW the
 * page before saving the pin (a pin grants no access), so a page the caller
 * can't see returns `false`. Idempotent server-side.
 */
export async function pinPage(pageId: string): Promise<boolean> {
  try {
    const res = await fetch('/api/eve/v1/pins', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ pageId }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Removes the user's pin for a page. Idempotent (unpinning a non-pin succeeds). */
export async function unpinPage(pageId: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/eve/v1/pins/${encodeURIComponent(pageId)}`, {
      method: 'DELETE',
      credentials: 'same-origin',
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Deletes a page the caller owns (server-scoped by `user_key`, so only their
 * own). Returns `true` on success; the caller updates the list optimistically
 * and re-syncs on `false`.
 */
export async function deletePage(id: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/eve/v1/pages/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'same-origin',
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Renames a page the caller owns (owner-gated server-side). Returns the stored
 * title (the server trims/caps/redacts, so it may differ from the input) or null
 * on failure — the caller reverts the optimistic label on null.
 */
export async function renamePage(id: string, title: string): Promise<string | null> {
  try {
    const res = await fetch(`/api/eve/v1/pages/${encodeURIComponent(id)}/title`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ title }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { title?: unknown };
    return typeof body.title === 'string' ? body.title : title;
  } catch {
    return null;
  }
}

/**
 * Turns nightly auto-refresh on/off for a page (`daily` ↔ `manual`), via the
 * same owner-gated schedule route the in-chat card's toggle uses. Best-effort →
 * `false` on failure (incl. a guest 403: daily is staff-only).
 */
export async function setPageDaily(id: string, daily: boolean): Promise<boolean> {
  try {
    const res = await fetch(`/api/eve/v1/artifacts/${encodeURIComponent(id)}/schedule`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ interval: daily ? 'daily' : 'manual' }),
    });
    return res.ok;
  } catch {
    return false;
  }
}
